/**
 * Organizations API Client
 * 조직 정보 조회 (ClinicContext용)
 *
 * Endpoints:
 * - GET /api/organizations/me       - 자신의 조직 조회 (POWER/USER)
 * - GET /api/admin/organizations    - 모든 조직 조회 (ADMIN)
 */

import apiClient from "./client";

/**
 * 조직 기능 플래그
 */
export interface ClinicFeatures {
  /**
   * 아웃바운드 기능 (BASIC 이상)
   */
  outbound: boolean;

  /**
   * AI 상담 분석 기능 (BASIC 이상)
   */
  aiConsult: boolean;

  /**
   * 고급 분석 기능 (PREMIUM 이상)
   */
  analytics: boolean;
}

export interface DayHours {
  open: string;
  close: string;
  closed: boolean;
  /**
   * 휴게 시작 시간 (예: "12:00")
   * V157: 점심/휴게시간 지원
   */
  breakStart?: string;
  /**
   * 휴게 종료 시간 (예: "13:00")
   * V157: 점심/휴게시간 지원
   */
  breakEnd?: string;
  /**
   * 접수마감 시간 (예: "17:30")
   * V1034: SCRUM-190 - 접수마감 설정 지원
   */
  lastOrder?: string;
  /**
   * 휴게시간 없음 여부
   * V259: true면 breakStart/breakEnd는 미설정으로 취급
   */
  noBreak?: boolean;
}

export interface BusinessHours {
  monday?: DayHours;
  tuesday?: DayHours;
  wednesday?: DayHours;
  thursday?: DayHours;
  friday?: DayHours;
  saturday?: DayHours;
  sunday?: DayHours;
}

/**
 * 휴무일 인터페이스
 * V1050: 정기 휴무 지원 - week/dayOfWeek 필드 추가
 */
export interface Holiday {
  date: string; // 휴무 날짜 (임시 휴무용, YYYY-MM-DD)
  reason: string; // 휴무 사유
  recurring: boolean; // 반복 여부 (정기 휴무 = true)

  // V1050: 정기 휴무 지원 필드
  week?: number; // 0=매주, 1=첫째주, 2=둘째주, 3=셋째주, 4=넷째주, 5=마지막주
  dayOfWeek?: number; // 0=일, 1=월, 2=화, 3=수, 4=목, 5=금, 6=토
}

/**
 * 조직 DTO
 */
export interface Organization {
  /**
   * 조직 ID
   */
  id: number;

  /**
   * 조직 고유 코드 (예: org_1, org_gangnam_dental)
   */
  code: string;

  /**
   * 조직명 (예: "강남클리어치과", "홍대미용실")
   */
  name: string;

  /**
   * 업종 코드 (예: "DENTAL", "BEAUTY")
   */
  businessTypeCode?: string;

  /**
   * 사업자 상호명 (예: "강남클리어치과의원")
   */
  businessName?: string;

  /**
   * 조직 대표 전화번호 (예: "02-1234-5678")
   */
  businessPhone?: string;

  /**
   * 조직 기능 플래그 (구독 등급 기반)
   */
  features?: ClinicFeatures;

  /**
   * 영업시간 (JSONB)
   */
  businessHours?: BusinessHours;

  /**
   * 휴무일 목록 (JSONB Array)
   */
  holidays?: Holiday[];
}

export const organizationsApi = {
  /**
   * 자신의 조직 조회 (POWER/USER)
   * GET /api/organizations/me
   *
   * @returns 자신의 조직 정보
   */
  getMyOrganization: async (): Promise<Organization> => {
    const response = await apiClient.get<Organization>("/api/organizations/me");
    return response.data;
  },

  /**
   * 모든 조직 조회 (ADMIN 전용)
   * GET /api/admin/organizations
   *
   * 활성 상태(ACTIVE)인 조직만 반환
   *
   * @returns 모든 조직 목록
   */
  getAllOrganizations: async (): Promise<Organization[]> => {
    const response = await apiClient.get<Organization[]>(
      "/api/admin/organizations",
    );
    return response.data;
  },

  /**
   * 내 조직 설정 수정 (영업시간, 휴무일)
   * PUT /api/organizations/me/settings
   */
  updateSettings: async (settings: {
    businessHours?: BusinessHours;
    holidays?: Holiday[];
  }): Promise<Organization> => {
    const response = await apiClient.put<Organization>(
      "/api/organizations/me/settings",
      settings,
    );
    return response.data;
  },
};
